import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { History } from 'history';

import { GeneralConstants } from 'app/constants/general-constants';
import { Taxonomy } from 'app/model/taxonomy';

import { User } from 'app/model/user';
import { taxonomyService } from 'app/services/taxonomy.service';
import { userService } from 'app/services/user.service';
import { AppThunk, RootState } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';

interface UserState {
    userList: User[];
    selected: ModelWithoutId<User>;
    possibleRoles: Record<string, string>;
    possibleTaxonomies: Taxonomy[];
}

const initialState: UserState = {
    userList: [],
    possibleRoles: {},
    possibleTaxonomies: [],
    selected: { first: '', last: '', username: '', roles: [], taxonomies: [] },
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserList: (state, action: PayloadAction<User[]>) => {
            state.userList = action.payload;
        },
        selectUser: (state, action: PayloadAction<User>) => {
            state.selected = action.payload;
        },
        prepareCreateUser: (state) => {
            state.selected = { first: '', last: '', username: '', roles: [], taxonomies: [] };
        },
        removeUser: (state, action: PayloadAction<User>) => {
            state.userList = state.userList.filter((user) => user.id !== action.payload.id);
        },
        setPossibleRoles: (state, action: PayloadAction<Record<string, string>>) => {
            state.possibleRoles = action.payload;
        },
        setPossibleTaxonomies: (state, action: PayloadAction<Taxonomy[]>) => {
            state.possibleTaxonomies = action.payload;
        },
    },
});

export const { setUserList, selectUser, prepareCreateUser, removeUser, setPossibleRoles, setPossibleTaxonomies } =
    userSlice.actions;

export const selectUserList = (state: RootState): User[] => state.users.userList;

export const selectSelectedUser = (state: RootState): ModelWithoutId<User> => state.users.selected;

export const selectPossibleRoles = (state: RootState): Record<string, string> => state.users.possibleRoles;
export const selectPossibleTaxonomies = (state: RootState): Taxonomy[] => state.users.possibleTaxonomies;

export const listUsers =
    (search?: string): AppThunk =>
    (dispatch) => {
        userService
            .list({
                pageSize: GeneralConstants.PAGE_SIZE_LARGE,
                extraParams: { complete: true },
                queryFilters: { search },
            })
            .subscribe((val) => {
                dispatch(setUserList(val.data));
            });
    };

export const loadUser =
    (id: string): AppThunk =>
    (dispatch) => {
        userService.get(id).subscribe((val) => {
            dispatch(selectUser(val));
        });
    };

export const updateUser =
    (user: User, history: History): AppThunk =>
    (dispatch) => {
        userService.save(user).subscribe((saved) => {
            if (!saved.id) {
                throw new Error('Saved user got no id');
            }
            dispatch(selectUser(saved));
            history.push(`/users/${saved.id}`);
        });
    };

export const deleteUser =
    (user: User): AppThunk =>
    (dispatch) => {
        userService.delete(user).subscribe(() => {
            dispatch(removeUser(user));
        });
    };

export const prepareRoles = (): AppThunk => (dispatch, getState) => {
    const state = getState();
    if (0 < Object.keys(state.users.possibleRoles).length) {
        // already loaded
        return;
    }
    userService.loadRoles().subscribe((roles) => {
        dispatch(setPossibleRoles(roles));
    });
};
export const prepareTaxonomies = (): AppThunk => (dispatch, getState) => {
    const state = getState();
    if (0 < state.users.possibleTaxonomies.length) {
        // already loaded
        return;
    }
    taxonomyService.listAll().subscribe((txs) => {
        dispatch(setPossibleTaxonomies(txs));
    });
};
